import {
  minimapControlAppearance,
  minimapControlBackground,
  minimapControlBorder,
  minimapControlBorderRadius,
  minimapControlBoxSizing,
  minimapControlColor,
  minimapControlFocusOutline,
  minimapControlFontFamily,
  minimapControlFontFeatureSettings,
  minimapControlFontSize,
  minimapControlFontStyle,
  minimapControlFontWeight,
  minimapControlGap,
  minimapControlHoverBackground,
  minimapControlHoverColor,
  minimapControlIconSize,
  minimapControlLineHeight,
  minimapControlMargin,
  minimapControlPadding,
  minimapControlPaddingValue,
  minimapControlSize,
  minimapControlTextAlign,
  minimapDockWidth,
  minimapDockWidthLarge,
  minimapDockWidthMedium,
  minimapPanelBorderRadius,
  minimapZoomPercentWidth,
} from '@blocksuite/affine-shared/styles';
import {
  resolveFitToScreenBound,
  stopPropagation,
} from '@blocksuite/affine-shared/utils';
import { IS_MAC } from '@blocksuite/global/env';
import { WithDisposable } from '@blocksuite/global/lit';
import { MinusIcon, PlusIcon, ViewBarIcon } from '@blocksuite/icons/lit';
import type { BlockStdScope } from '@blocksuite/std';
import { GfxControllerIdentifier, ZOOM_STEP } from '@blocksuite/std/gfx';
import { effect } from '@preact/signals-core';
import { css, html, LitElement, nothing, unsafeCSS } from 'lit';
import { property, query, state } from 'lit/decorators.js';

export class EdgelessZoomToolbar extends WithDisposable(LitElement) {
  static override styles = css`
    :host {
      display: flex;
    }

    .edgeless-zoom-toolbar-container {
      display: flex;
      align-items: center;
      background: transparent;
      border-radius: ${unsafeCSS(minimapPanelBorderRadius)};
      fill: currentcolor;
      padding: ${unsafeCSS(minimapControlPadding)};
    }

    .edgeless-zoom-toolbar-container.horizantal {
      flex-direction: row;
    }

    .edgeless-zoom-toolbar-container.vertical {
      flex-direction: column;
      width: ${unsafeCSS(minimapDockWidth)};
      background: transparent;
      padding: 0;
      border: none;
      box-shadow: none;
    }

    /* Responsive minimap container widths */
    @container viewport (width > 1024px) and (width <= 1440px) {
      .edgeless-zoom-toolbar-container.vertical {
        width: ${unsafeCSS(minimapDockWidthMedium)};
      }
    }

    @container viewport (width > 1440px) {
      .edgeless-zoom-toolbar-container.vertical {
        width: ${unsafeCSS(minimapDockWidthLarge)};
      }
    }

    .edgeless-zoom-toolbar-container.vertical .zoom-controls {
      --edgeless-tool-icon-border-radius: ${unsafeCSS(
        minimapControlBorderRadius
      )};
      --edgeless-tool-icon-color: ${unsafeCSS(minimapControlColor)};
      --edgeless-tool-icon-hover-background: ${unsafeCSS(
        minimapControlHoverBackground
      )};
      --edgeless-tool-icon-hover-color: ${unsafeCSS(minimapControlHoverColor)};
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: center;
      gap: ${unsafeCSS(minimapControlGap)};
      width: 100%;
    }

    .edgeless-zoom-toolbar-container.vertical .navigation-hint {
      width: 100%;
      margin-top: 4px;
      color: ${unsafeCSS(minimapControlColor)};
      font-family: ${unsafeCSS(minimapControlFontFamily)};
      font-size: 9px;
      font-style: ${unsafeCSS(minimapControlFontStyle)};
      font-weight: ${unsafeCSS(minimapControlFontWeight)};
      font-feature-settings: ${unsafeCSS(minimapControlFontFeatureSettings)};
      line-height: 14px;
      text-align: center;
      white-space: nowrap;
      opacity: 0.72;
      pointer-events: none;
    }

    .edgeless-zoom-toolbar-container.vertical .navigation-key {
      display: inline-flex;
      min-width: 14px;
      height: 14px;
      align-items: center;
      justify-content: center;
      padding: 0 3px;
      border: 1px solid currentcolor;
      border-radius: 3px;
      box-sizing: border-box;
      font: inherit;
      font-weight: 600;
      line-height: 12px;
      opacity: 0.9;
    }

    .edgeless-zoom-toolbar-container.vertical .navigation-hint > span {
      display: block;
    }

    .edgeless-zoom-toolbar-container[level='second'] {
      position: absolute;
      top: 100%;
      margin-top: 8px;
    }

    .edgeless-zoom-toolbar-container[hidden] {
      display: none;
    }

    .zoom-percent,
    .zoom-control-button {
      display: flex;
      align-items: center;
      justify-content: center;
      width: ${unsafeCSS(minimapControlSize)};
      height: ${unsafeCSS(minimapControlSize)};
      appearance: ${unsafeCSS(minimapControlAppearance)};
      margin: ${unsafeCSS(minimapControlMargin)};
      border: ${unsafeCSS(minimapControlBorder)};
      box-sizing: ${unsafeCSS(minimapControlBoxSizing)};
      padding: ${unsafeCSS(minimapControlPadding)};
      color: ${unsafeCSS(minimapControlColor)};
      background-color: ${unsafeCSS(minimapControlBackground)};
      border-radius: ${unsafeCSS(minimapControlBorderRadius)};
      cursor: pointer;
      white-space: nowrap;
      font-size: ${unsafeCSS(minimapControlFontSize)};
      font-style: ${unsafeCSS(minimapControlFontStyle)};
      font-weight: ${unsafeCSS(minimapControlFontWeight)};
      font-feature-settings: ${unsafeCSS(minimapControlFontFeatureSettings)};
      line-height: ${unsafeCSS(minimapControlLineHeight)};
      text-align: ${unsafeCSS(minimapControlTextAlign)};
      font-family: ${unsafeCSS(minimapControlFontFamily)};
    }

    .zoom-percent {
      width: ${unsafeCSS(minimapZoomPercentWidth)};
    }

    .zoom-control-button svg {
      width: ${unsafeCSS(minimapControlIconSize)};
      height: ${unsafeCSS(minimapControlIconSize)};
    }

    .zoom-percent:hover,
    .zoom-control-button:hover {
      color: ${unsafeCSS(minimapControlHoverColor)};
      background-color: ${unsafeCSS(minimapControlHoverBackground)};
    }

    .zoom-percent:focus-visible,
    .zoom-control-button:focus-visible {
      outline: ${unsafeCSS(minimapControlFocusOutline)};
      outline-offset: 2px;
    }

    .zoom-percent[disabled],
    .zoom-control-button[disabled] {
      pointer-events: none;
      cursor: not-allowed;
      color: var(--streamlain-text-disable-color);
    }

    /* Minimal wrapper; the minimap component owns its internal styles. */
    .minimap-wrapper {
      width: 100%;
      margin-bottom: 8px;
    }
  `;

  get gfx() {
    return this.std.get(GfxControllerIdentifier);
  }

  get edgelessTool() {
    return this.gfx.tool.currentToolOption$.peek();
  }

  get locked() {
    return this.viewport.locked;
  }

  get viewport() {
    return this.gfx.viewport;
  }

  setZoomByStep = (step: number) => {
    this.viewport.zoomByStep(step);
  };

  fitSelectionOrScreen = () => {
    const selectedBounds = this.gfx.selection.selectedElements
      .map(element =>
        resolveFitToScreenBound(element, this.gfx.view.get(element))
      )
      .filter(bound => bound.w > 0 && bound.h > 0);

    if (selectedBounds.length > 0) {
      this.gfx.fitToScreen({
        bounds: selectedBounds,
        smooth: true,
        padding: [0, 0, 0, 0],
      });
      return;
    }

    this.gfx.fitToScreen({
      smooth: true,
      padding: [0, 0, 0, 0],
    });
  };

  get zoom() {
    if (!this.viewport) {
      console.error('Something went wrong, viewport is not available');
      return 1;
    }
    return this.viewport.zoom;
  }

  private _isVerticalBar() {
    return this.layout === 'vertical';
  }

  @query('edgeless-minimap-canvas')
  private accessor _minimap!: Element | null;

  @state()
  private accessor _maximapOpen = false;

  override connectedCallback() {
    super.connectedCallback();

    this.disposables.add(
      effect(() => {
        this.gfx.tool.currentToolName$.value;
        this.requestUpdate();
      })
    );
  }

  override firstUpdated() {
    const { disposables } = this;
    disposables.add(
      this.viewport.viewportUpdated.subscribe(() => this.requestUpdate())
    );
  }

  override render() {
    // Hide entire zoom toolbar + minimap during presentation mode. Read-only
    // users still need viewport navigation, which does not mutate document data.
    if (this.gfx.tool.currentToolName$.value === 'frameNavigator') {
      return nothing;
    }

    const formattedZoom = `${Math.round(this.zoom * 100)}%`;
    const classes = `edgeless-zoom-toolbar-container ${this.layout}`;
    const locked = this.locked;

    const verticalZoomControls = html`
      <button
        aria-label="Fit to screen"
        class="zoom-control-button"
        title="Fit to screen"
        @click=${this.fitSelectionOrScreen}
        ?disabled=${locked}
      >
        ${ViewBarIcon()}
      </button>
      <button
        aria-label="Zoom out"
        class="zoom-control-button"
        title="Zoom out"
        @click=${() => this.setZoomByStep(-ZOOM_STEP)}
        ?disabled=${locked}
      >
        ${MinusIcon()}
      </button>
      <button
        class="zoom-percent"
        @click=${() => this.viewport.smoothZoom(1)}
        ?disabled=${locked}
      >
        ${formattedZoom}
      </button>
      <button
        aria-label="Zoom in"
        class="zoom-control-button"
        title="Zoom in"
        @click=${() => this.setZoomByStep(ZOOM_STEP)}
        ?disabled=${locked}
      >
        ${PlusIcon()}
      </button>
      <button
        type="button"
        class="zoom-control-button"
        aria-label="Gesamtübersicht öffnen"
        title="Gesamtübersicht öffnen"
        aria-haspopup="dialog"
        aria-expanded=${this._maximapOpen}
        @click=${() => this._minimap?.dispatchEvent(new Event('open-maximap'))}
      >
        <svg
          viewBox="0 0 24 24"
          aria-hidden="true"
          fill="none"
          stroke="currentColor"
          stroke-width="1.8"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path
            d="M8 3H3v5m13-5h5v5M3 16v5h5m13-5v5h-5M3 3l6 6m12-6-6 6M3 21l6-6m12 6-6-6"
          />
        </svg>
      </button>
    `;

    const horizontalZoomControls = html`
      <edgeless-tool-icon-button
        .tooltip=${'Fit to screen'}
        .tipPosition=${'top-end'}
        .arrow=${true}
        @click=${this.fitSelectionOrScreen}
        .iconContainerPadding=${minimapControlPaddingValue}
        .iconSize=${minimapControlIconSize}
        .disabled=${locked}
      >
        ${ViewBarIcon()}
      </edgeless-tool-icon-button>
      <edgeless-tool-icon-button
        .tooltip=${'Zoom out'}
        .tipPosition=${'top'}
        .arrow=${true}
        @click=${() => this.setZoomByStep(-ZOOM_STEP)}
        .iconContainerPadding=${minimapControlPaddingValue}
        .iconSize=${minimapControlIconSize}
        .disabled=${locked}
      >
        ${MinusIcon()}
      </edgeless-tool-icon-button>
      <button
        class="zoom-percent"
        @click=${() => this.viewport.smoothZoom(1)}
        .disabled=${locked}
      >
        ${formattedZoom}
      </button>
      <edgeless-tool-icon-button
        .tooltip=${'Zoom in'}
        .tipPosition=${'top'}
        .arrow=${true}
        @click=${() => this.setZoomByStep(ZOOM_STEP)}
        .iconContainerPadding=${minimapControlPaddingValue}
        .iconSize=${minimapControlIconSize}
        .disabled=${locked}
      >
        ${PlusIcon()}
      </edgeless-tool-icon-button>
    `;

    return html`
      <div
        class=${classes}
        @dblclick=${stopPropagation}
        @mousedown=${stopPropagation}
        @mouseup=${stopPropagation}
        @pointerdown=${stopPropagation}
      >
        ${this._isVerticalBar()
          ? html`
              <!-- Product minimap component -->
              <div class="minimap-wrapper">
                <edgeless-minimap-canvas
                  .std=${this.std}
                  @maximap-open-changed=${(event: CustomEvent<boolean>) => {
                    this._maximapOpen = event.detail;
                  }}
                ></edgeless-minimap-canvas>
              </div>
              <div class="zoom-controls">${verticalZoomControls}</div>
              <div class="navigation-hint">
                <span>Scroll: Pan · Shift + Scroll: Horizontal pan</span>
                <span>
                  <kbd class="navigation-key">${IS_MAC ? '⌘' : 'Ctrl'}</kbd>
                  + Scroll: Zoom · Pinch: Zoom
                </span>
              </div>
            `
          : horizontalZoomControls}
      </div>
    `;
  }

  @property({ attribute: false })
  accessor layout: 'horizontal' | 'vertical' = 'horizontal';

  @property({ attribute: false })
  accessor std!: BlockStdScope;
}
